import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, InputNumber, Select, Space, message, Popconfirm, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

// ---------------------------------------------------------------------------
// Tier sub-table rendered inside expanded status rows
// ---------------------------------------------------------------------------
function TiersPanel({ record, isAdmin, onChanged }) {
  const [tierModal, setTierModal] = useState(false);
  const [editTier, setEditTier]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();

  const tiers = [...(record.Tiers || [])].sort((a, b) => a.WorkedMinsMax - b.WorkedMinsMax);

  const openCreate = () => {
    setEditTier(null);
    form.resetFields();
    setTierModal(true);
  };

  const openEdit = (tier) => {
    setEditTier(tier);
    form.setFieldsValue({ worked_mins_max: tier.WorkedMinsMax, max_break_mins: tier.MaxBreakMins });
    setTierModal(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editTier) {
        await api.put(`/api/admin/ccaas-statuses/${record.ID}/tiers/${editTier.ID}`, {
          worked_mins_max: values.worked_mins_max,
          max_break_mins:  values.max_break_mins,
        });
        message.success('Tier updated.');
      } else {
        await api.post(`/api/admin/ccaas-statuses/${record.ID}/tiers`, {
          worked_mins_max: values.worked_mins_max,
          max_break_mins:  values.max_break_mins,
        });
        message.success('Tier added.');
      }
      setTierModal(false);
      onChanged();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save tier.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tier) => {
    try {
      await api.delete(`/api/admin/ccaas-statuses/${record.ID}/tiers/${tier.ID}`);
      message.success('Tier deleted.');
      onChanged();
    } catch {
      message.error('Failed to delete tier.');
    }
  };

  const tierCols = [
    {
      title: 'Shift worked less than',
      dataIndex: 'WorkedMinsMax',
      key: 'WorkedMinsMax',
      render: (v) => `${v} mins`,
    },
    {
      title: 'Max break allowed',
      dataIndex: 'MaxBreakMins',
      key: 'MaxBreakMins',
      width: 170,
      render: (v) => <Tag color="blue">{v} mins</Tag>,
    },
    ...(isAdmin ? [{
      title: '',
      key: 'actions',
      width: 100,
      render: (_, tier) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(tier)} />
          <Popconfirm title="Delete this tier?" onConfirm={() => handleDelete(tier)} okText="Delete" okType="danger">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: '8px 24px 16px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong style={{ fontSize: 12 }}>
          Shift-length break tiers
          {tiers.length > 0 && (
            <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
              — when tiers are set, Max Mins / Day is ignored for this state
            </Typography.Text>
          )}
        </Typography.Text>
        {isAdmin && (
          <Button size="small" icon={<PlusOutlined />} onClick={openCreate}>
            Add Tier
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={tiers}
        columns={tierCols}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No tiers — flat Max Mins / Day applies.' }}
        style={{ maxWidth: 560 }}
      />

      <Modal maskClosable={false}
        title={editTier ? 'Edit Tier' : 'Add Tier'}
        open={tierModal}
        onOk={handleSave}
        onCancel={() => setTierModal(false)}
        confirmLoading={saving}
        okText={editTier ? 'Save' : 'Add'}
        width={380}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            label="Agent worked less than (mins)"
            name="worked_mins_max"
            rules={[{ required: true, message: 'Required' }]}
            extra="Upper bound of the shift-duration bracket (exclusive). Use a large number like 9999 for the catch-all."
          >
            <InputNumber min={1} max={9999} style={{ width: '100%' }} placeholder="e.g. 240" />
          </Form.Item>
          <Form.Item
            label="Max break minutes allowed"
            name="max_break_mins"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 15" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CCaaSStatusesAdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';
  const [rows, setRows]       = useState([]);
  const [teams, setTeams]     = useState([]);
  const [awsColumns, setAwsColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/admin/ccaas-statuses')
      .then((r) => setRows(r.data))
      .catch(() => message.error('Failed to load CCaaS statuses.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/api/admin/teams')
      .then((r) => setTeams(r.data))
      .catch(() => {});
    api.get('/api/admin/aws-state-columns')
      .then((r) => setAwsColumns(r.data || []))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      external_id:       record.ExternalID || '',
      state:             record.State,
      team_id:           record.TeamID ?? null,
      aws_column:        record.AWSColumn ?? null,
      max_mins_day:      record.MaxMinsDay ?? null,
      max_count_per_day: record.MaxCountPerDay ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        external_id:       values.external_id || null,
        state:             values.state,
        team_id:           values.team_id ?? null,
        aws_column:        values.aws_column ?? null,
        max_mins_day:      values.max_mins_day ?? null,
        max_count_per_day: values.max_count_per_day ?? null,
      };
      if (editItem) {
        await api.put(`/api/admin/ccaas-statuses/${editItem.ID}`, body);
        message.success('Status updated.');
      } else {
        await api.post('/api/admin/ccaas-statuses', body);
        message.success('Status created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/ccaas-statuses/${id}`);
      message.success('Deleted.');
      load();
    } catch {
      message.error('Failed to delete.');
    }
  };

  const columns = [
    { title: 'State',           dataIndex: 'State',          key: 'State',          ellipsis: true },
    { title: 'Team',            dataIndex: 'TeamName',       key: 'TeamName',       width: 180,
      render: (v) => v || <Typography.Text type="secondary">All Teams</Typography.Text> },
    { title: 'AWS Column',      dataIndex: 'AWSColumn',      key: 'AWSColumn',      width: 190,
      render: (v) => v
        ? <Tag color="blue" style={{ margin: 0 }}>{awsColumns.find((c) => c.value === v)?.label || v}</Tag>
        : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: 'External ID',     dataIndex: 'ExternalID',     key: 'ExternalID',     width: 160,
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Max Mins / Day',  dataIndex: 'MaxMinsDay',     key: 'MaxMinsDay',     width: 140, align: 'right',
      render: (v, r) => {
        if (r.Tiers?.length > 0) return <Tag color="purple">Tiered</Tag>;
        return v != null ? v : <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    { title: 'Max Count / Day', dataIndex: 'MaxCountPerDay', key: 'MaxCountPerDay', width: 140, align: 'right',
      render: (v) => v != null ? v : <Typography.Text type="secondary">—</Typography.Text> },
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete this status?" onConfirm={() => handleDelete(r.ID)} okText="Delete" okType="danger">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>CCaaS Status Thresholds</Typography.Title>
        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Status</Button>}
      </div>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Maps CCaaS state names to violation thresholds. State must match exactly the value in AgentStateData.State.
      </Typography.Text>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No statuses configured.' }}
        expandable={{
          expandedRowRender: (record) => (
            <TiersPanel record={record} isAdmin={isAdmin} onChanged={load} />
          ),
          rowExpandable: () => true,
        }}
      />

      <Modal maskClosable={false}
        title={editItem ? 'Edit CCaaS Status' : 'Add CCaaS Status'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editItem ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="State" name="state" rules={[{ required: true, message: 'State is required' }]}
            extra="Must match exactly the State value in Superior.AgentStateData (e.g. Lunch, Break)">
            <Input placeholder="e.g. Lunch" />
          </Form.Item>
          <Form.Item label="Team" name="team_id"
            extra="Leave blank to apply to all teams; select a team to override thresholds for that team only">
            <Select
              allowClear
              placeholder="All Teams"
              options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item label="AWS Column" name="aws_column"
            extra="Optional. Also apply this threshold to the AWS State by Day view on DBAL, against this CNEDailyStats column. The column is stored rather than the name because the two systems name the same state differently -- CCaaS Break is AWS On Break.">
            <Select
              allowClear
              placeholder="Not applied to AWS"
              options={awsColumns}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item label="External ID" name="external_id"
            extra="Optional reference ID from the CCaaS system">
            <Input placeholder="Optional" allowClear />
          </Form.Item>
          <Form.Item label="Max Minutes Per Day" name="max_mins_day"
            extra="Alert when total minutes in this state on a single day exceeds this value. Ignored if shift-length tiers are configured below.">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 30" />
          </Form.Item>
          <Form.Item label="Max Count Per Day" name="max_count_per_day"
            extra="Alert when the number of occurrences per day exceeds this value">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 3" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
